import type { AgentMessagePart, ActionButtonDef } from '../../types/agent-messages';
import type { ParserContext, ParserResult } from './types';

const DECISION_PATTERNS = [
  /would you like to/i,
  /what would you like to do/i,
  /ready to proceed/i,
  /please choose/i,
  /select an option/i,
  /how would you like to proceed/i,
  /what sounds most helpful/i,
  /what would you prefer/i,
  /which option/i,
  /choose one/i,
];

const BRACKET_OPTION_RE = /\[([^\]]+)\]/g;
const MD_LINK_RE = /\[([^\]]+)\]\((https?:\/\/[^)]+)\)/g;

const BOLD_OPTION_RE = /\*\*(?:Option\s*\d+)\*\*\s*[:：-]\s*(?:\*\*)?(.+?)(?:\*\*)?(?:\s*[-–—]\s*.+)?$/gm;
const NUMBERED_BOLD_RE = /^\d+\.\s+\*\*(.+?)\*\*\s*[-–—:]\s*(.+)$/gm;

/**
 * Extracts actionCard parts from decision-prompt text.
 *
 * Handles three patterns:
 * 1. Markdown links -> buttons that open URLs
 * 2. Bold numbered options like **Option 1**: Continue... -> buttons
 * 3. Bracket options like [Generate Code] -> buttons that send as message
 */
export function actionCardParser(ctx: ParserContext): ParserResult {
  const text = ctx.text.trim();
  if (!text) return { parts: [], remainingText: text };

  const parts: AgentMessagePart[] = [];

  // First: extract markdown links as action buttons
  const linkButtons = extractMarkdownLinks(text);
  if (linkButtons.length > 0) {
    const cleanedText = text.replace(MD_LINK_RE, (_, label) => label);
    parts.push({
      type: 'actionCard',
      description: 'Quick actions:',
      buttons: linkButtons,
    });
    return { parts, remainingText: cleanedText.trim() };
  }

  // Second: bold numbered options (Option 1: ... or 1. **Continue**)
  const boldOptions = extractBoldOptions(text);
  if (boldOptions.length >= 2) {
    const cleanedText = removeBoldOptionBlock(text);
    parts.push({
      type: 'actionCard',
      description: 'Choose an option:',
      buttons: boldOptions,
    });
    return { parts, remainingText: cleanedText.trim() };
  }

  // Third: bracket-option decision prompts
  const paragraphs = text.split(/\n\n+/);
  const lastParagraph = paragraphs[paragraphs.length - 1];

  const isDecisionPrompt = DECISION_PATTERNS.some((p) => p.test(lastParagraph));
  if (!isDecisionPrompt) {
    return { parts: [], remainingText: text };
  }

  const options: string[] = [];
  let match: RegExpExecArray | null;
  const re = new RegExp(BRACKET_OPTION_RE.source, BRACKET_OPTION_RE.flags);
  while ((match = re.exec(lastParagraph)) !== null) {
    options.push(match[1].trim());
  }

  if (options.length === 0) {
    return { parts: [], remainingText: text };
  }

  const buttons: ActionButtonDef[] = options.map((label, i) => ({
    label,
    style: i === 0 ? 'primary' : 'normal',
    action: label,
  }));

  const remainingParagraphs = paragraphs.slice(0, -1);
  const remainingText = remainingParagraphs.join('\n\n').trim();

  const description = lastParagraph.replace(BRACKET_OPTION_RE, '').replace(/\s+/g, ' ').trim();

  const part: AgentMessagePart = {
    type: 'actionCard',
    description: description || 'Choose an action:',
    buttons,
  };

  return { parts: [part], remainingText };
}

function extractMarkdownLinks(text: string): ActionButtonDef[] {
  const buttons: ActionButtonDef[] = [];
  let match: RegExpExecArray | null;
  const re = new RegExp(MD_LINK_RE.source, MD_LINK_RE.flags);

  while ((match = re.exec(text)) !== null) {
    const label = match[1].trim();
    const url = match[2].trim();
    if (label && url) {
      buttons.push({
        label,
        style: buttons.length === 0 ? 'primary' : 'normal',
        action: url,
      });
    }
  }

  return buttons;
}

function extractBoldOptions(text: string): ActionButtonDef[] {
  const buttons: ActionButtonDef[] = [];

  let re = new RegExp(BOLD_OPTION_RE.source, BOLD_OPTION_RE.flags);
  let match: RegExpExecArray | null;
  while ((match = re.exec(text)) !== null) {
    const label = match[1].replace(/\*\*/g, '').trim();
    if (label) {
      buttons.push({
        label,
        style: buttons.length === 0 ? 'primary' : 'normal',
        action: label,
      });
    }
  }
  if (buttons.length >= 2) return buttons;

  buttons.length = 0;
  re = new RegExp(NUMBERED_BOLD_RE.source, NUMBERED_BOLD_RE.flags);
  while ((match = re.exec(text)) !== null) {
    const label = match[1].trim();
    if (label) {
      buttons.push({
        label,
        style: buttons.length === 0 ? 'primary' : 'normal',
        action: label,
      });
    }
  }

  return buttons;
}

function removeBoldOptionBlock(text: string): string {
  const lines = text.split('\n');
  const filtered = lines.filter((line) => {
    const boldRe = new RegExp(BOLD_OPTION_RE.source, BOLD_OPTION_RE.flags);
    const numRe = new RegExp(NUMBERED_BOLD_RE.source, NUMBERED_BOLD_RE.flags);
    if (boldRe.test(line)) return false;
    if (numRe.test(line)) return false;
    return true;
  });
  while (filtered.length > 0) {
    const last = filtered[filtered.length - 1].trim();
    if (!last || DECISION_PATTERNS.some((p) => p.test(last))) {
      filtered.pop();
    } else {
      break;
    }
  }
  return filtered.join('\n');
}
